import React, {
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Alert, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import {
	getAllClubsApi,
	getOpenMatchesApi,
	getState,
} from "../../api/api";
import {
	SPORTS,
	SPORT_LABELS,
	ALLOWED_DURATIONS,
	DURATION_LABELS,
	SURFACES,
	SURFACE_LABELS,
	SERVICES,
	SERVICE_LABELS,
	ENCLOSURES,
	ENCLOSURE_LABELS,
} from "../../constants/courtDomain";
import { MOBILE_MAX_WIDTH } from "../../constants/breakpoints";
import useCitiesByState from "../../hooks/useCitiesByState";
import RIcon from "../reservas/components/ReservasIcons";
import { SucursalesChip } from "../reservas/components/Sucursales";
import {
	SingleDatePickerPill,
	PillDropdown,
	MultiPillDropdown,
	FALLBACK_IMAGE,
	ReservationKindTabs,
	formatPillDateLabel,
} from "../reservas/components/ReservaShared";
import OrganizerTimePillSelect from "../../organizer/components/OrganizerTimePillSelect";
import { toMapPoints } from "./components/mapConfig";
import QuickSlotModal from "./components/QuickSlotModal";
import { RatingBadge } from "../reservas/components/RatingStars";
import { RATING_RANGE_OPTIONS, ratingRangeLabel, ratingRangesParam } from "../reservas/components/reviewsModel";
import OpenMatchCard from "./components/OpenMatchCard";
import {
	MATCH_KINDS,
	MATCH_KIND_LABELS,
	MISSING_OPTIONS,
	buildOpenMatchesParams,
	normalizeOpenMatches,
} from "./openMatchesModel";
import "../reservas/reservas.css";

// The only static reference to the map chunk — react-leaflet/leaflet are
// fetched lazily the first time a buyer toggles the map on.
const CanchasMapView = React.lazy(() => import("./components/CanchasMapView"));

const normalizeClubs = (response) => {
	const info = response?.data?.info;
	return Array.isArray(info) ? info : [];
};

// The card advertises the cheapest court price ("Desde $X"), not a deposit:
// the deposit now lives on each pricing row, so no single club-wide figure
// describes it. `priceFrom` is what the listing endpoint already computes.
const priceFromClub = (club) => {
	const amount = Number(club?.priceFrom) || 0;
	return `$${amount.toLocaleString("es-AR")}`;
};

const Canchas = () => {
	const navigate = useNavigate();
	// Vista activa ("Clubes" | "Partidos abiertos") reflejada en la URL como `?vista=partidos`; la
	// página no sincroniza el resto de los filtros a la URL, solo esta elección (para compartir el link).
	const [searchParams, setSearchParams] = useSearchParams();
	// `?crear=partido`: modo "elegir club para crear un partido abierto". Siempre muestra la vista de
	// clubes (pisa a `?vista=partidos`), pide solo clubes con partidos abiertos habilitados y cada ficha
	// lleva directo al flujo de creación (`/canchas/:id?crear=partido`).
	const createMode = searchParams.get("crear") === "partido";
	const view = !createMode && searchParams.get("vista") === "partidos" ? "matches" : "clubs";

	const [clubs, setClubs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const latestClubsRequestId = useRef(0);

	// Filter state
	const [nameFilter, setNameFilter] = useState("");
	const [stateId, setStateId] = useState("");
	const [cityId, setCityId] = useState("");
	const [sport, setSport] = useState("");
	// Display-only default: pre-fills the date filter with tomorrow (ATC-style
	// pre-filled search bar) without triggering a date-filtered fetch on mount.
	// See loadClubs/handleSearch below — `date` is only ever sent as part of an
	// explicit "Buscar canchas" submit.
	const [selectedDate, setSelectedDate] = useState(() =>
		dayjs().add(1, "day").startOf("day"),
	);
	const [timeFilter, setTimeFilter] = useState("");
	// True once the buyer has clicked "Buscar canchas" at least once. Gates
	// nearby-slot pill rendering so pills never appear from the tomorrow
	// display-default or from automatic province/city reloads.
	const [hasExplicitSearched, setHasExplicitSearched] = useState(false);
	// { club, slot } of the pill the buyer last clicked — consumed by
	// <QuickSlotModal> once it is wired in (see design Phase 3).
	const [quickSlotSelection, setQuickSlotSelection] = useState(null);

	const [selectedSurfaces, setSelectedSurfaces] = useState([]);
	const [selectedDurations, setSelectedDurations] = useState([]);
	const [selectedServices, setSelectedServices] = useState([]);
	const [selectedEnclosures, setSelectedEnclosures] = useState([]);
	// Selected rating-range tokens (`4-5`, `3-4`, `0-3`), sent as the CSV `ratingRanges`.
	const [ratingRanges, setRatingRanges] = useState([]);
	// Filtros propios de "Partidos abiertos": tipos (`kind`) y cuántos jugadores faltan (`missing`).
	const [selectedKinds, setSelectedKinds] = useState([]);
	const [selectedMissing, setSelectedMissing] = useState([]);

	// Estado de la vista "Partidos abiertos". Es independiente del de clubes para no pisar su listado.
	const [matches, setMatches] = useState([]);
	const [matchesHasMore, setMatchesHasMore] = useState(false);
	const [matchesTotal, setMatchesTotal] = useState(0);
	const [matchesPage, setMatchesPage] = useState(1);
	const [matchesLoading, setMatchesLoading] = useState(false);
	const [matchesLoadingMore, setMatchesLoadingMore] = useState(false);
	const [matchesError, setMatchesError] = useState("");
	const latestMatchesRequestId = useRef(0);

	const [showSecondary, setShowSecondary] = useState(false);
	const [showMap, setShowMap] = useState(false);

	const [states, setStates] = useState([]);
	// Cities depend on the selected province: never load the full catalog.
	const { cities } = useCitiesByState(stateId);

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });

	const minDate = dayjs().startOf("day");
	const maxDate = minDate.add(60, "day");

	const loadCatalogs = useCallback(async () => {
		try {
			const stRes = await getState();
			setStates(Array.isArray(stRes?.data?.info) ? stRes.data.info : []);
		} catch (_) {}
	}, []);

	const loadClubs = useCallback(
		async (overrideParams = {}) => {
			const requestId = ++latestClubsRequestId.current;
			try {
				setLoading(true);
				setError("");
				const params = {};

				const st = overrideParams.hasOwnProperty("stateId")
					? overrideParams.stateId
					: stateId;
				const ct = overrideParams.hasOwnProperty("cityId")
					? overrideParams.cityId
					: cityId;
				const sp = overrideParams.hasOwnProperty("sport")
					? overrideParams.sport
					: sport;
				// `date` is deliberately NOT derived from `selectedDate` by default — the
				// "tomorrow" pre-fill is display-only. It is only sent when a caller
				// (handleSearch, on explicit "Buscar canchas" submit) opts in via
				// overrideParams. Mount and automatic province/city reloads never send it.
				const dt = overrideParams.hasOwnProperty("date")
					? overrideParams.date
					: "";
				const tm = overrideParams.hasOwnProperty("time")
					? overrideParams.time
					: timeFilter;
				const surf = overrideParams.hasOwnProperty("surfaces")
					? overrideParams.surfaces
					: selectedSurfaces;
				const dur = overrideParams.hasOwnProperty("durations")
					? overrideParams.durations
					: selectedDurations;
				const serv = overrideParams.hasOwnProperty("services")
					? overrideParams.services
					: selectedServices;
				const enc = overrideParams.hasOwnProperty("enclosures")
					? overrideParams.enclosures
					: selectedEnclosures;

				const rt = overrideParams.hasOwnProperty("ratingRanges")
					? overrideParams.ratingRanges
					: ratingRanges;

				if (st) params.stateid = st;
				if (ct) params.cityid = ct;
				if (sp) params.sport = sp;
				if (dt) params.date = dt;
				if (tm) params.time = tm;
				if (surf && surf.length) params.surface = surf.join(",");
				if (dur && dur.length) params.durationMinutes = dur.join(",");
				if (serv && serv.length) params.services = serv.join(",");
				if (enc && enc.length) params.enclosure = enc.join(",");
				if (rt && rt.length) params.ratingRanges = ratingRangesParam(rt);
				// Back T18: solo clubes con `openMatches.enabled`; compone con el resto de los filtros.
				const createOn = overrideParams.hasOwnProperty("createMode")
					? overrideParams.createMode
					: createMode;
				if (createOn) params.openMatches = "enabled";

				const response = await getAllClubsApi(params);
				if (requestId === latestClubsRequestId.current) {
					setClubs(normalizeClubs(response));
				}
			} catch (err) {
				if (requestId === latestClubsRequestId.current) {
					setError(
						err?.response?.data?.message ||
							"No pudimos cargar las canchas disponibles.",
					);
				}
			} finally {
				if (requestId === latestClubsRequestId.current) {
					setLoading(false);
				}
			}
		},
		[
			stateId,
			cityId,
			sport,
			selectedDate,
			timeFilter,
			selectedSurfaces,
			selectedDurations,
			selectedServices,
			selectedEnclosures,
			ratingRanges,
			createMode,
		],
	);

	// Carga "Partidos abiertos" con los MISMOS filtros que la vista de clubes (mismas reglas: `date` solo
	// viaja en una búsqueda explícita, el resto se aplica al pedir). `overrides` pisa el estado actual para
	// el handler que acaba de cambiarlo; `page > 1` agrega a la lista ("Ver más") en vez de reemplazarla.
	const loadMatches = useCallback(
		async (overrides = {}, page = 1) => {
			const requestId = ++latestMatchesRequestId.current;
			const pick = (key, current) =>
				overrides.hasOwnProperty(key) ? overrides[key] : current;
			const append = page > 1;
			try {
				if (append) setMatchesLoadingMore(true);
				else setMatchesLoading(true);
				setMatchesError("");
				const params = buildOpenMatchesParams(
					{
						stateId: pick("stateId", stateId),
						cityId: pick("cityId", cityId),
						sport: pick("sport", sport),
						// Igual que en clubes: el "mañana" del selector es solo visual.
						date: pick("date", ""),
						time: pick("time", timeFilter),
						surfaces: pick("surfaces", selectedSurfaces),
						durations: pick("durations", selectedDurations),
						services: pick("services", selectedServices),
						enclosures: pick("enclosures", selectedEnclosures),
						ratingRanges: pick("ratingRanges", ratingRanges),
						kinds: pick("kinds", selectedKinds),
						missing: pick("missing", selectedMissing),
					},
					page,
				);
				const response = await getOpenMatchesApi(params);
				if (requestId !== latestMatchesRequestId.current) return;
				const data = normalizeOpenMatches(response);
				setMatches((curr) => (append ? [...curr, ...data.matches] : data.matches));
				setMatchesPage(data.page);
				setMatchesHasMore(data.hasMore);
				setMatchesTotal(data.total);
			} catch (err) {
				if (requestId === latestMatchesRequestId.current) {
					setMatchesError(
						err?.response?.data?.message ||
							"No pudimos cargar los partidos abiertos.",
					);
				}
			} finally {
				if (requestId === latestMatchesRequestId.current) {
					setMatchesLoading(false);
					setMatchesLoadingMore(false);
				}
			}
		},
		[
			stateId,
			cityId,
			sport,
			timeFilter,
			selectedSurfaces,
			selectedDurations,
			selectedServices,
			selectedEnclosures,
			ratingRanges,
			selectedKinds,
			selectedMissing,
		],
	);

	// Recarga la vista activa; los handlers de filtro la usan sin saber en qué vista están.
	const reload = (overrides = {}) =>
		view === "matches" ? loadMatches(overrides) : loadClubs(overrides);

	useEffect(() => {
		loadCatalogs();
	}, [loadCatalogs]);

	// Initial load only. Province/city changes trigger their own reload (see
	// handleStateChange/handleCityChange below) and "Buscar canchas" triggers
	// handleSearch. Every other filter is applied on-demand, not live, so
	// loadClubs must NOT be re-run just because its identity changed.
	useEffect(() => {
		reload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Cambiar de vista conserva los filtros y recarga la vista a la que se entra.
	const handleViewChange = (next) => {
		if (next === view) return;
		const wantsMatches = next === "matches";
		setSearchParams(
			(prev) => {
				const out = new URLSearchParams(prev);
				if (wantsMatches) out.set("vista", "partidos");
				else out.delete("vista");
				out.delete("crear");
				return out;
			},
			{ replace: true },
		);
		if (wantsMatches) loadMatches();
		else loadClubs({ createMode: false });
	};

	// Entra al modo crear desde "Crear partido abierto": vista de clubes + `crear=partido`.
	const enterCreateMode = () => {
		setSearchParams(
			(prev) => {
				const out = new URLSearchParams(prev);
				out.delete("vista");
				out.set("crear", "partido");
				return out;
			},
			{ replace: true },
		);
		loadClubs({ createMode: true });
	};

	// Sale del modo crear y vuelve al listado normal de clubes (los filtros se conservan).
	const exitCreateMode = () => {
		setSearchParams(
			(prev) => {
				const out = new URLSearchParams(prev);
				out.delete("crear");
				return out;
			},
			{ replace: true },
		);
		loadClubs({ createMode: false });
	};

	// En modo crear, cada club (ficha, botón o marcador del mapa) abre el flujo de creación.
	const goToClub = (to) => navigate(createMode ? `${to}?crear=partido` : to);

	const handleStateChange = (val) => {
		setStateId(val);
		setCityId(""); // Reset selected city; its options reload through useCitiesByState
		reload({ stateId: val, cityId: "" });
	};

	const handleCityChange = (val) => {
		setCityId(val);
		reload({ cityId: val });
	};

	const handleRatingChange = useCallback(
		(val) => {
			setRatingRanges(val);
			reload({ ratingRanges: val });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[loadClubs, loadMatches, view],
	);

	const filteredClubs = useMemo(() => {
		if (!nameFilter.trim()) return clubs;
		return clubs.filter((c) =>
			(c.name || "").toLowerCase().includes(nameFilter.toLowerCase()),
		);
	}, [clubs, nameFilter]);

	const mapPoints = useMemo(() => toMapPoints(filteredClubs), [filteredClubs]);

	// Guard against a stuck-open map: if the current filters drop to zero
	// results while the map is visible, close it instead of leaving an empty
	// map panel with a now-disabled toggle.
	useEffect(() => {
		if (filteredClubs.length === 0 && showMap) {
			setShowMap(false);
		}
	}, [filteredClubs.length, showMap]);

	const handleSearch = () => {
		setHasExplicitSearched(true);
		reload({ date: selectedDate ? selectedDate.format("YYYY-MM-DD") : "" });
	};

	// Quita un tipo / cantidad de la selección y recarga con el valor nuevo (los chips no esperan a "Buscar").
	const removeKind = (kind) => {
		const next = selectedKinds.filter((k) => k !== kind);
		setSelectedKinds(next);
		loadMatches({ kinds: next });
	};
	const removeMissing = (value) => {
		const next = selectedMissing.filter((m) => m !== value);
		setSelectedMissing(next);
		loadMatches({ missing: next });
	};

	// Estado vacío: vuelve todos los filtros a su valor por defecto y recarga sin ninguno.
	const handleClearFilters = () => {
		setNameFilter("");
		setStateId("");
		setCityId("");
		setSport("");
		setSelectedDate(null);
		setTimeFilter("");
		setSelectedSurfaces([]);
		setSelectedDurations([]);
		setSelectedServices([]);
		setSelectedEnclosures([]);
		setRatingRanges([]);
		setSelectedKinds([]);
		setSelectedMissing([]);
		loadMatches({
			stateId: "",
			cityId: "",
			sport: "",
			date: "",
			time: "",
			surfaces: [],
			durations: [],
			services: [],
			enclosures: [],
			ratingRanges: [],
			kinds: [],
			missing: [],
		});
	};

	const handleOpenMatch = useCallback(
		(match) => navigate(`/partidos/${match._id}`),
		[navigate],
	);

	// Nombre del club: sin filtro en el back (igual que clubes), se filtra en el cliente sobre lo cargado.
	const filteredMatches = useMemo(() => {
		if (!nameFilter.trim()) return matches;
		return matches.filter((m) =>
			(m.club?.name || m.clubName || "")
				.toLowerCase()
				.includes(nameFilter.toLowerCase()),
		);
	}, [matches, nameFilter]);

	const handleSlotPillClick = useCallback((club, slot) => {
		setQuickSlotSelection({ club, slot });
	}, []);

	const handleQuickSlotModalHide = useCallback(() => {
		setQuickSlotSelection(null);
	}, []);

	// Build the option arrays
	const SPORT_OPTIONS = useMemo(
		() => [
			{ value: "", label: "Todos" },
			...SPORTS.map((s) => ({ value: s, label: SPORT_LABELS[s] || s })),
		],
		[],
	);

	const SURFACE_OPTIONS = useMemo(
		() => SURFACES.map((s) => ({ value: s, label: SURFACE_LABELS[s] || s })),
		[],
	);

	const DURATION_OPTIONS = useMemo(
		() =>
			ALLOWED_DURATIONS.map((d) => ({
				value: String(d),
				label: DURATION_LABELS[d] || `${d} min`,
			})),
		[],
	);

	const SERVICE_OPTIONS = useMemo(
		() => SERVICES.map((s) => ({ value: s, label: SERVICE_LABELS[s] || s })),
		[],
	);

	const KIND_OPTIONS = useMemo(
		() => MATCH_KINDS.map((k) => ({ value: k, label: MATCH_KIND_LABELS[k] })),
		[],
	);

	const ENCLOSURE_OPTIONS = useMemo(
		() =>
			ENCLOSURES.map((e) => ({ value: e, label: ENCLOSURE_LABELS[e] || e })),
		[],
	);

	// Build chips list
	const chips = useMemo(() => {
		const list = [];
		if (sport) {
			list.push({
				icon: "ball",
				label: SPORT_LABELS[sport] || sport,
				onClear: () => setSport(""),
			});
		}
		if (selectedDate) {
			list.push({
				icon: "cal",
				label: formatPillDateLabel(selectedDate),
				onClear: () => setSelectedDate(null),
			});
		}
		if (timeFilter) {
			list.push({
				icon: "clock",
				label: `${timeFilter} hs`,
				onClear: () => setTimeFilter(""),
			});
		}
		selectedSurfaces.forEach((surf) => {
			list.push({
				icon: "layers",
				label: SURFACE_LABELS[surf] || surf,
				onClear: () =>
					setSelectedSurfaces((curr) => curr.filter((s) => s !== surf)),
			});
		});
		selectedDurations.forEach((dur) => {
			list.push({
				icon: "clock",
				label: DURATION_LABELS[dur] || `${dur} min`,
				onClear: () =>
					setSelectedDurations((curr) =>
						curr.filter((d) => String(d) !== String(dur)),
					),
			});
		});
		selectedServices.forEach((serv) => {
			list.push({
				icon: "building",
				label: SERVICE_LABELS[serv] || serv,
				onClear: () =>
					setSelectedServices((curr) => curr.filter((s) => s !== serv)),
			});
		});
		selectedEnclosures.forEach((enc) => {
			list.push({
				icon: "tent",
				label: ENCLOSURE_LABELS[enc] || enc,
				onClear: () =>
					setSelectedEnclosures((curr) => curr.filter((e) => e !== enc)),
			});
		});
		ratingRanges.forEach((range) => {
			list.push({
				icon: "star",
				label: `Valoración ${ratingRangeLabel(range)}`,
				onClear: () => handleRatingChange(ratingRanges.filter((r) => r !== range)),
			});
		});
		if (view === "matches") {
			selectedKinds.forEach((kind) => {
				list.push({
					icon: "users",
					label: MATCH_KIND_LABELS[kind] || kind,
					onClear: () => removeKind(kind),
				});
			});
			selectedMissing.forEach((value) => {
				const opt = MISSING_OPTIONS.find((o) => o.value === value);
				list.push({
					icon: "users",
					label: opt ? opt.label : value,
					onClear: () => removeMissing(value),
				});
			});
		}
		return list;
	}, [
		sport,
		selectedDate,
		timeFilter,
		selectedSurfaces,
		selectedDurations,
		selectedServices,
		selectedEnclosures,
		ratingRanges,
		handleRatingChange,
		view,
		selectedKinds,
		selectedMissing,
		// eslint-disable-next-line react-hooks/exhaustive-deps
	]);

	return (
		<div className="reservas-root">
			<div className="wrap">
				<ReservationKindTabs active="canchas" />

				{/* Header */}
				<div className="res-head">
					<div className="res-actions">
						<div>
							<div className="eyebrow">Canchas</div>
							<h1>Reservá tu cancha</h1>
						</div>
					</div>
					<p className="lead">
						Encontrá la cancha perfecta para tu deporte y asegurá tu turno en
						segundos. Sin cargos por servicio.
					</p>
				</div>

				{/* Vista: clubes o partidos abiertos (los filtros de abajo se comparten) */}
					<div className="canchas-view-toggle" role="tablist" aria-label="Vista del listado">
						<button
							type="button"
							role="tab"
							aria-selected={view === "clubs"}
							className={view === "clubs" ? "is-active" : ""}
							onClick={() => handleViewChange("clubs")}
						>
							Clubes
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={view === "matches"}
							className={view === "matches" ? "is-active" : ""}
							onClick={() => handleViewChange("matches")}
						>
							Partidos abiertos
						</button>
					</div>

					{/* Filters Container */}
				<div className="res-filters-container">
					{/* Main Filters */}
					<div className="res-filters canchas-filters-main">
						<div className="rf rf-triple">
							<label htmlFor="canchas-name-filter">Nombre del club</label>
							<div className="rf-control">
								<RIcon name="search" />
								<input
									id="canchas-name-filter"
									placeholder="Opcional"
									value={nameFilter}
									onChange={(e) => setNameFilter(e.target.value)}
								/>
							</div>
						</div>

						<div className="rf">
							<label>Provincia</label>
							<PillDropdown
								iconName="map"
								value={stateId}
								options={[
									{ value: "", label: "Todas" },
									...states.map((s) => ({ value: s._id, label: s.name })),
								]}
								onChange={handleStateChange}
								placeholder="Todas"
								ariaLabel="Provincia"
							/>
						</div>

						<div className="rf">
							<label>Ciudad</label>
							<PillDropdown
								iconName="pin"
								value={cityId}
								options={[
									{ value: "", label: "Todas" },
									...cities.map((c) => ({ value: c._id, label: c.name })),
								]}
								onChange={handleCityChange}
								placeholder="Todas"
								searchable={true}
								disabled={!stateId}
								ariaLabel="Ciudad"
							/>
						</div>

						<div className="rf">
							<label>Deporte</label>
							<PillDropdown
								iconName="ball"
								value={sport}
								options={SPORT_OPTIONS}
								onChange={setSport}
								placeholder="Todos"
								ariaLabel="Deporte"
							/>
						</div>

						<div className="rf">
							<label>Fecha</label>
							<SingleDatePickerPill
								value={selectedDate}
								onChange={setSelectedDate}
								minDate={minDate}
								maxDate={maxDate}
								ariaLabel="Fecha"
							/>
						</div>

						<div className="rf">
							<label>Horario</label>
							<OrganizerTimePillSelect
								value={timeFilter}
								onChange={(t) => setTimeFilter(t)}
								placeholder="--:--"
								triggerClassName="ef-date-pill-trigger"
								ariaLabel="Horario"
								minutes={["00", "30"]}
								singleList={true}
							/>
						</div>

						<div className="rf rf-search rf-double-btn">
							<button className="r-btn-primary" onClick={handleSearch}>
								<RIcon name="search" width={17} height={17} /> {view === "matches" ? "Buscar partidos" : "Buscar canchas"}
								</button>
							<button
								className="r-btn-soft"
								type="button"
								onClick={() => setShowSecondary(!showSecondary)}
							>
								<RIcon name="filter" width={15} height={15} />
								{showSecondary ? "Menos filtros" : "Más filtros"}
							</button>
						</div>
					</div>

					{/* Secondary Filters */}
					{showSecondary && (
						<div className="res-filters canchas-filters-secondary canchas-filters--rating">
							<div className="rf">
								<label>Superficie</label>
								<MultiPillDropdown
									iconName="layers"
									value={selectedSurfaces}
									options={SURFACE_OPTIONS}
									onChange={setSelectedSurfaces}
									placeholder="Cualquiera"
									ariaLabel="Superficie"
								/>
							</div>

							<div className="rf">
								<label>Duración</label>
								<MultiPillDropdown
									iconName="clock"
									value={selectedDurations}
									options={DURATION_OPTIONS}
									onChange={setSelectedDurations}
									placeholder="Cualquiera"
									ariaLabel="Duración"
								/>
							</div>

							<div className="rf">
								<label>Servicios del club</label>
								<MultiPillDropdown
									iconName="building"
									value={selectedServices}
									options={SERVICE_OPTIONS}
									onChange={setSelectedServices}
									placeholder="Cualquiera"
									ariaLabel="Servicios del club"
								/>
							</div>

							<div className="rf">
								<label>Cerramiento</label>
								<MultiPillDropdown
									iconName="tent"
									value={selectedEnclosures}
									options={ENCLOSURE_OPTIONS}
									onChange={setSelectedEnclosures}
									placeholder="Cualquiera"
									ariaLabel="Cerramiento"
								/>
							</div>

							<div className="rf">
								<label>Valoración</label>
								<MultiPillDropdown
									iconName="star"
									value={ratingRanges}
									options={RATING_RANGE_OPTIONS}
									onChange={handleRatingChange}
									placeholder="Todas"
									ariaLabel="Valoración"
								/>
							</div>

							{view === "matches" && (
								<>
									<div className="rf">
										<label>Tipo de partido</label>
										<MultiPillDropdown
											iconName="users"
											value={selectedKinds}
											options={KIND_OPTIONS}
											onChange={setSelectedKinds}
											placeholder="Cualquiera"
											ariaLabel="Tipo de partido"
										/>
									</div>

									<div className="rf">
										<label>Cantidad de jugadores</label>
										<MultiPillDropdown
											iconName="users"
											value={selectedMissing}
											options={MISSING_OPTIONS}
											onChange={setSelectedMissing}
											placeholder="Cualquiera"
											ariaLabel="Cantidad de jugadores"
										/>
									</div>
								</>
							)}
</div>
					)}
				</div>

				{createMode && (
					<div className="canchas-create-banner" role="status">
						<span>Elegí el club donde querés armar tu partido</span>
						<button type="button" className="r-btn-soft" onClick={exitCreateMode}>
							Salir
						</button>
					</div>
				)}

				{/* Result meta */}
				<div className="res-meta">
					<div className="count">
						{view === "matches" ? (
							<>
								<b>
									{nameFilter.trim() ? filteredMatches.length : matchesTotal}{" "}
									{(nameFilter.trim() ? filteredMatches.length : matchesTotal) === 1
										? "partido abierto"
										: "partidos abiertos"}
								</b>{" "}
								para sumarte
							</>
						) : (
							<>
								<b>{filteredClubs.length} clubes</b> con canchas disponibles
							</>
						)}
					</div>
					{view === "clubs" && (
						<button
							type="button"
							className={`canchas-map-toggle ${showMap ? "is-active" : ""}`}
							disabled={filteredClubs.length === 0}
							aria-disabled={filteredClubs.length === 0}
							onClick={() => setShowMap((v) => !v)}
						>
							<RIcon name="map" width={15} height={15} />
							{showMap ? "Ocultar el mapa" : "Mostrar el mapa"}
						</button>
					)}
					{chips.length > 0 && (
						<div className="chips">
							{chips.map((c, i) => (
								<span
									className="q-chip"
									key={i}
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: "6px",
									}}
								>
									<RIcon name={c.icon} width={13} height={13} />
									{c.label}
									<button
										type="button"
										aria-label={`Quitar filtro ${c.label}`}
										onClick={c.onClear}
										style={{
											background: "none",
											border: "none",
											padding: 0,
											marginLeft: "4px",
											color: "var(--zoco-gray-2)",
											cursor: "pointer",
											display: "inline-flex",
											alignItems: "center",
										}}
									>
										<RIcon name="x" width={10} height={10} />
									</button>
								</span>
							))}
						</div>
					)}
				</div>

				{view === "matches" ? (
					<>
						{matchesError && <Alert variant="danger">{matchesError}</Alert>}
						{matchesLoading ? (
							<div style={{ textAlign: "center", padding: "60px 0" }}>
								<Spinner animation="border" />
							</div>
						) : filteredMatches.length === 0 ? (
							<div className="empty-state">
								<RIcon name="users" width={48} height={48} />
								<h3>No hay partidos abiertos con estos filtros</h3>
								<p>Probá con otros filtros o creá vos el partido.</p>
								<div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
									<button
										type="button"
										className="r-btn-soft"
										onClick={handleClearFilters}
									>
										Limpiar filtros
									</button>
									{/* Sin club de contexto: abre el listado de clubes habilitados en modo crear (`?crear=partido`). */}
									<button
										type="button"
										className="r-btn-primary"
										onClick={enterCreateMode}
										title="Elegí un club para crear tu partido."
									>
										Crear partido abierto
									</button>
								</div>
							</div>
						) : (
							<>
								<div className="bars-grid">
									{filteredMatches.map((m) => (
										<OpenMatchCard key={String(m._id)} match={m} onOpen={handleOpenMatch} />
									))}
								</div>
								{matchesHasMore && (
									<div className="canchas-load-more">
										<button
											type="button"
											className="r-btn-soft"
											disabled={matchesLoadingMore}
											onClick={() => loadMatches({}, matchesPage + 1)}
										>
											{matchesLoadingMore ? "Cargando..." : "Ver más partidos"}
										</button>
									</div>
								)}
							</>
						)}
					</>
				) : (
				<>
				{error && <Alert variant="danger">{error}</Alert>}

				{loading ? (
					<div style={{ textAlign: "center", padding: "60px 0" }}>
						<Spinner animation="border" />
					</div>
				) : filteredClubs.length === 0 ? (
					<div className="empty-state">
						<RIcon name="court" width={48} height={48} />
						<h3>No encontramos canchas</h3>
						<p>Probá con otros filtros o volvé más tarde.</p>
					</div>
				) : showMap ? (
					isMobile ? (
						<div className="canchas-map-full">
							<Suspense fallback={<Spinner animation="border" />}>
								<CanchasMapView
									points={mapPoints}
									onMarkerClick={(p) => goToClub(`/canchas/${p.slugOrId}`)}
								/>
							</Suspense>
						</div>
					) : (
						<div className="canchas-split">
							<div className="canchas-split-list">
								<ClubsGrid
									list={filteredClubs}
									navigate={goToClub}
									createMode={createMode}
									showSlots={hasExplicitSearched && !createMode}
									onSlotClick={handleSlotPillClick}
								/>
							</div>
							<div className="canchas-split-map">
								<Suspense fallback={<Spinner animation="border" />}>
									<CanchasMapView
										points={mapPoints}
										onMarkerClick={(p) => goToClub(`/canchas/${p.slugOrId}`)}
									/>
								</Suspense>
							</div>
						</div>
					)
				) : (
					<ClubsGrid
						list={filteredClubs}
						navigate={goToClub}
						createMode={createMode}
						showSlots={hasExplicitSearched && !createMode}
						selectedSport={sport}
						onSlotClick={handleSlotPillClick}
					/>
				)}

				</>
				)}

				<div style={{ height: 60 }} />
			</div>

			{quickSlotSelection && (
				<QuickSlotModal
					show={true}
					club={quickSlotSelection.club}
					slot={quickSlotSelection.slot}
					dateLabel={selectedDate ? selectedDate.format("YYYY-MM-DD") : ""}
					selectedSportFilter={sport}
					onHide={handleQuickSlotModalHide}
					isMobile={isMobile}
				/>
			)}
		</div>
	);
};

function ClubsGrid({
	list,
	navigate,
	showSlots = false,
	createMode = false,
	selectedSport = "",
	onSlotClick,
}) {
	return (
		<div className="bars-grid">
			{list.map((club) => {
				const slugOrId = club.slug || club._id;
				const img = club.image || FALLBACK_IMAGE;
				const priceFrom = priceFromClub(club);
				const sports = Array.isArray(club.sports) ? club.sports : [];
				// Pills only render after an explicit time search (showSlots) AND
				// when the backend actually returned nearbySlots for this club
				// (gated server-side on `time` presence — filtered by selectedSport if set).
				const rawSlots =
					showSlots && Array.isArray(club.nearbySlots) ? club.nearbySlots : [];
				const filteredSlots = selectedSport
					? rawSlots.filter((s) => !s.sport || s.sport === selectedSport)
					: rawSlots;
				const nearbySlots = filteredSlots.slice(0, 4);
				return (
					<div
						className="bar-card"
						key={String(club._id)}
						onClick={() => navigate(`/canchas/${slugOrId}`)}
					>
						<div className="bar-media">
							<img
								src={img}
								alt={club.name || "Club"}
								onError={(e) => {
									e.currentTarget.src = FALLBACK_IMAGE;
								}}
							/>
							<div className="bar-media-deposit">
								<span className="deposit-label">Desde</span>
								<span className="deposit-val">{priceFrom}</span>
							</div>
						</div>
						<div className="bar-body">
							<div className="bar-name">{club.name}</div>
							<SucursalesChip count={club.sucursalesCount} />
							{Number(club.ratingCount) > 0 && (
								<div className="rv-card-rating">
									<RatingBadge avg={club.ratingAvg} count={club.ratingCount} />
								</div>
							)}
							{club.address && (
								<div className="bar-loc">
									<RIcon name="pin" width={14} height={14} />
									{club.address}
								</div>
							)}
							{sports.length > 0 && (
								<div className="bar-tags">
									{sports.slice(0, 3).map((s) => (
										<span className="bar-tag" key={s}>
											{SPORT_LABELS[s] || s}
										</span>
									))}
								</div>
							)}
							{nearbySlots.length > 0 && (
								<div className="bar-slots">
									{nearbySlots.map((slot) => (
										<button
											type="button"
											className="slot-btn"
											key={slot.slotStart}
											data-testid="quick-slot-pill"
											onClick={(e) => {
												e.stopPropagation();
												onSlotClick?.(club, slot);
											}}
										>
											{slot.slotStart}
										</button>
									))}
								</div>
							)}
							<div className="bar-foot centered">
								<button
									className="r-btn-primary bar-action-btn"
									onClick={(e) => {
										e.stopPropagation();
										navigate(`/canchas/${slugOrId}`);
									}}
								>
									{createMode ? "Elegir club" : "Ver canchas"}
								</button>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}

export default Canchas;
